// Thin client for the Mushi admin API.
//
// The dashboard lives behind MU_ADMIN_TOKEN. For local development a token is
// stored in localStorage under `mushi.adminToken` and sent on every admin
// request. A 401 response clears the token so callers can react to it.

export interface Project {
  id: string;
  key: string;
  name: string;
  description: string;
  color: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiKeySummary {
  id: string;
  name: string;
  prefix: string;
  permissions: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface ReportSummary {
  id: string;
  reference: string;
  title: string;
  status: string;
  severity: string;
  source: string;
  gameVersion: string;
  buildNumber: string;
  platform: string;
  environment: string;
  receivedAt: string;
  updatedAt: string;
  attachmentCount: number;
  hasPayload: boolean;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

const ADMIN_TOKEN_KEY = "mushi.adminToken";

export function getAdminToken(): string {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setAdminToken(token: string) {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAdminToken();
  const headers = new Headers(init.headers);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("Content-Type") && init.body && typeof init.body === "string") {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  // Errors are JSON shaped like { code, message }; successful
  // responses are decoded as the caller's T below.
  interface ErrorBody {
    code?: string;
    message?: string;
  }
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { code: "bad_response", message: text };
    }
  }
  if (!res.ok) {
    if (res.status === 401) {
      // Token was rejected or missing; clear so the auth guard can react.
      setAdminToken("");
    }
    const errBody = (parsed ?? null) as ErrorBody | null;
    throw new ApiError(
      res.status,
      errBody?.code ?? `http_${res.status}`,
      errBody?.message ?? `HTTP ${res.status}`,
    );
  }
  return parsed as T;
}

export interface AttachmentMeta {
  id: string;
  reportId: string;
  projectId: string;
  originalName: string;
  contentType: string;
  size: number;
  checksum: string;
  category: string;
  status: string;
  createdAt: string;
  previewKind: "image" | "text" | "none";
}

export interface ReportActivityEntry {
  id: string;
  type: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  body?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// WebhookEvent is the closed set of events webhooks can subscribe to.
export type WebhookEvent = "report.created";
// WebhookType identifies the payload template the dispatcher renders.
export type WebhookType = "discord";

// McpTokenSummary is the row shape returned by the MCP tokens endpoint.
// Revoked tokens still appear (with revokedAt set) so the dashboard
// can show "revoked" badges.
export interface McpTokenSummary {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  lastUsedAt?: string;
  revokedAt?: string;
}

// McpTokenCreated extends McpTokenSummary with the one-time plaintext
// token and the full URL the user can paste into their MCP client.
// The backend returns these only in the POST response.
export interface McpTokenCreated extends McpTokenSummary {
  token: string;
  url: string;
}

// WebhookSummary is the row shape returned by the webhooks endpoint. The
// secret is only present on the create response.
export interface WebhookSummary {
  id: string;
  projectId: string;
  name: string;
  type: WebhookType;
  url: string;
  secret?: string;
  events: WebhookEvent[];
  enabled: boolean;
  lastTriggeredAt?: string;
  lastStatus?: number;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReportDetail {
  id: string;
  projectId: string;
  reference: string;
  title: string;
  description: string;
  status: string;
  severity: string;
  source: string;
  gameVersion: string;
  buildNumber: string;
  platform: string;
  environment: string;
  playerRef: string;
  externalId: string;
  assignee: string;
  receivedAt: string;
  updatedAt: string;
  payloadJson: string;
  attachments: AttachmentMeta[];
  activity?: ReportActivityEntry[];
}

export const api = {
  health(): Promise<{ status: string }> {
    return request("/healthz");
  },
  listProjects(): Promise<{ projects: Project[] }> {
    return request("/api/admin/v1/projects");
  },
  createProject(input: {
    key: string;
    name: string;
    description?: string;
    color?: string;
  }): Promise<Project> {
    return request("/api/admin/v1/projects", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateProject(
    projectId: string,
    input: { name?: string; description?: string; color?: string }
  ): Promise<Project> {
    return request(`/api/admin/v1/projects/${projectId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  deleteProject(projectId: string): Promise<void> {
    return request(`/api/admin/v1/projects/${projectId}`, {
      method: "DELETE",
    });
  },
  listApiKeys(projectId: string): Promise<{ keys: ApiKeySummary[] }> {
    return request(`/api/admin/v1/projects/${projectId}/api-keys`);
  },
  createApiKey(
    projectId: string,
    name: string
  ): Promise<{ id: string; name: string; token: string; prefix: string; createdAt: string }> {
    return request(`/api/admin/v1/projects/${projectId}/api-keys`, {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  },
  revokeApiKey(projectId: string, keyId: string): Promise<{ id: string; revokedAt: string }> {
    return request(`/api/admin/v1/projects/${projectId}/api-keys/${keyId}`, {
      method: "DELETE",
    });
  },
  listReports(projectId: string): Promise<{ reports: ReportSummary[] }> {
    return request(`/api/admin/v1/projects/${projectId}/reports`);
  },
  getReport(projectId: string, reportId: string): Promise<ReportDetail> {
    return request(`/api/admin/v1/projects/${projectId}/reports/${reportId}`);
  },
  deleteReport(projectId: string, reportId: string): Promise<void> {
    return request(`/api/admin/v1/projects/${projectId}/reports/${reportId}`, {
      method: "DELETE",
    });
  },
  updateReport(
    projectId: string,
    reportId: string,
    patch: {
      status?: string;
      severity?: string;
      title?: string;
      description?: string;
      assignee?: string;
    }
  ): Promise<ReportDetail> {
    return request(`/api/admin/v1/projects/${projectId}/reports/${reportId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  getAttachment(attachmentId: string): Promise<AttachmentMeta> {
    return request(`/api/admin/v1/attachments/${attachmentId}`);
  },
  listWebhooks(projectId: string): Promise<{ webhooks: WebhookSummary[] }> {
    return request(`/api/admin/v1/projects/${projectId}/webhooks`);
  },
  createWebhook(
    projectId: string,
    input: {
      name: string;
      type: WebhookType;
      url: string;
      secret: string;
      events?: WebhookEvent[];
    },
  ): Promise<WebhookSummary> {
    return request(`/api/admin/v1/projects/${projectId}/webhooks`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateWebhook(
    projectId: string,
    webhookId: string,
    patch: {
      name?: string;
      url?: string;
      secret?: string;
      events?: WebhookEvent[];
      enabled?: boolean;
    },
  ): Promise<WebhookSummary> {
    return request(
      `/api/admin/v1/projects/${projectId}/webhooks/${webhookId}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
  },
  deleteWebhook(projectId: string, webhookId: string): Promise<void> {
    return request(
      `/api/admin/v1/projects/${projectId}/webhooks/${webhookId}`,
      { method: "DELETE" },
    );
  },
  testWebhook(
    projectId: string,
    webhookId: string,
  ): Promise<{ webhookId: string; status: number; error?: string }> {
    return request(
      `/api/admin/v1/projects/${projectId}/webhooks/${webhookId}/test`,
      { method: "POST" },
    );
  },
  attachmentDownloadUrl(attachmentId: string): string {
    return `/api/admin/v1/attachments/${attachmentId}/download`;
  },
  // -- MCP tokens ------------------------------------------------------------
  // MCP tokens are global credentials: anyone with the URL gets full
  // admin-scope access via the streamable HTTP transport. The plaintext
  // token is returned ONLY in the create response; afterwards only
  // metadata + a per-row revoke action are exposed.
  listMcpTokens(): Promise<{ tokens: McpTokenSummary[] }> {
    return request("/api/admin/v1/mcp-tokens");
  },
  createMcpToken(name: string): Promise<McpTokenCreated> {
    return request("/api/admin/v1/mcp-tokens", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  },
  revokeMcpToken(id: string): Promise<{ id: string; revokedAt: string }> {
    return request(`/api/admin/v1/mcp-tokens/${id}`, {
      method: "DELETE",
    });
  },
  // fetchAttachment downloads the raw bytes. Returns the response object so
  // the caller can stream it into a blob URL. The Authorization header is
  // applied manually because attachments are not loaded through JSON.
  async fetchAttachment(attachmentId: string): Promise<Response> {
    const token = getAdminToken();
    const headers = new Headers();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(`/api/admin/v1/attachments/${attachmentId}/download`, { headers });
    if (!res.ok) {
      const text = await res.text();
      interface ErrorBody {
        code?: string;
        message?: string;
      }
      let body: ErrorBody | null = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch {
        body = { message: text };
      }
      throw new ApiError(res.status, body?.code ?? `http_${res.status}`, body?.message ?? `HTTP ${res.status}`);
    }
    return res;
  },
};